import React, { useState, useEffect } from "react";
import { useController } from "react-hook-form";

import StarterKit from "@tiptap/starter-kit";
import { useEditor } from "@tiptap/react";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import Link from "@tiptap/extension-link";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuButtonBulletedList,
  MenuButtonCodeBlock,
  MenuButtonIndent,
  MenuButtonUnindent,
  MenuButtonOrderedList,
  MenuButtonHorizontalRule,
  MenuButtonUnderline,
  MenuButtonRedo,
  MenuButtonUndo,
  MenuButton,
  MenuButtonEditLink,
  LinkBubbleMenu,
  RichTextEditorProvider,
  RichTextField,
  LinkBubbleMenuHandler,
  MenuButtonImageUpload,
} from "mui-tiptap";

import { RichTextContainer } from "./richText.style";

import { getText } from "../../../utils";

const RichText = ({
  control,
  name,
  required,
  error,
  maxCharacters,
  handleUploadImage,
  copyPasteRestricted,
  disabled,
}) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);

  const { field } = useController({
    name,
    control,
    defaultValue: "",
    rules: {
      required: {
        value: required,
        message: "This field is required",
      },
      validate: {
        maxCharacters: (value) => {
          if (maxCharacters && getText(value).length > maxCharacters) {
            return `This field must be less than ${maxCharacters} characters`;
          }
          return true;
        },
        req: (value) => {
          if (required && getText(value).length <= 0) {
            return "This field is required";
          }
          return true;
        },
      },
    },
  });
  const [textHTML, setTextHTML] = useState(field.value);

  const CustomLinkExtension = Link.extend({
    inclusive: false,
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image,
      Youtube,
      CustomLinkExtension.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
      LinkBubbleMenuHandler,
    ],
    content: textHTML,
    editorProps: {
      handlePaste: () => {
        if (copyPasteRestricted) return true;
      },
      handleDrop: () => {
        if (copyPasteRestricted) return true;
      },
      attributes: {
        class: "editor",
      },
    },
    onUpdate: ({ editor }) => {
      setTextHTML(editor.getHTML());
      field.onChange(editor.getHTML());
    },
    onBlur: () => {
      field.onBlur();
    },
  });

  useEffect(() => {
    if (disabled === undefined) return;
    if (disabled) {
      editor?.setEditable(false);
    } else {
      editor?.setEditable(true);
    }
  }, [disabled, editor]);

  const onUploadFiles = async (files) =>
    await Promise.all(
      files.map(async (file) => ({
        src: await handleUploadImage(file),
        alt: file.name,
      })),
    );

  return (
    <RichTextContainer error={error ? 1 : 0} disabled={disabled ? 1 : 0}>
      <input type="hidden" name={field.name} value={textHTML} />
      {loaded && (
        <RichTextEditorProvider editor={editor}>
          <LinkBubbleMenu />
          <RichTextField
            controls={
              <MenuControlsContainer className="controls-container">
                <MenuSelectHeading />
                <MenuDivider />
                <MenuButtonBold />
                <MenuButtonUnderline />
                <MenuButtonItalic />
                <MenuDivider />
                <MenuButtonBulletedList />
                <MenuButtonOrderedList />
                <MenuButtonIndent />
                <MenuButtonUnindent />
                <MenuButtonCodeBlock />
                <MenuDivider />
                <MenuButtonEditLink />
                <MenuButtonImageUpload
                  inputProps={{
                    multiple: false,
                    accept: "image/jpeg, image/png, image/gif, image/tiff",
                  }}
                  onUploadFiles={onUploadFiles}
                />
                <MenuButton
                  tooltipLabel="Add Video"
                  onClick={() => {
                    const url = window.prompt("Video URL");
                    if (url) {
                      editor
                        ?.chain()
                        .focus()
                        .setYoutubeVideo({ src: url })
                        .run();
                    }
                  }}
                  IconComponent={OndemandVideoIcon}
                  selected={false}
                />
                <MenuButtonHorizontalRule />
                <MenuDivider />
                <MenuButtonUndo />
                <MenuButtonRedo />
              </MenuControlsContainer>
            }
          />
        </RichTextEditorProvider>
      )}
      <span className="error">{error?.message || ""}</span>
    </RichTextContainer>
  );
};

export default RichText;
